import { LDateTime } from '@app/components/date/LDateTime';
import {
  confidenceToLevel,
  distanceToLevel,
} from '@app/modules/persons/appearances/confidence-level';
import { PersonAppearance, PersonLane } from '@app/modules/persons/appearances/types';
import { ConfidenceBadge } from '@app/modules/persons/components/ConfidenceBadge';
import { Seconds, toDateTimeStr } from '@mi-front/core';
import FaceOutlinedIcon from '@mui/icons-material/FaceOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { Duration } from '@ui/components';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { CopyLinkAtTimestampButton } from '../../../popup/CopyLinkAtTimestampButton';
import { PlayAtTimestampButton } from '../../../popup/PlayAtTimestampButton';
import { usePersonsTrackCapture } from '../hooks/usePersonsTrackCapture';

type PersonsViewAppearanceRowProps = {
  lane: PersonLane;
  appearance: PersonAppearance;
  /** Whether this row matches the timeline hover. */
  highlighted: boolean;
  onHover: () => void;
};

/**
 * One appearance row in the Personas detail list.
 *
 * Time, face/voice source chip, duration, confidence, play + copy link.
 * Play/copy capture with `surface: 'results_table'` (not the track).
 */
export function PersonsViewAppearanceRow({
  lane,
  appearance,
  highlighted,
  onHover,
}: PersonsViewAppearanceRowProps) {
  const isFace = appearance.source === 'face';
  const { playFromAppearanceClicked, copyLinkClicked } = usePersonsTrackCapture();

  const handlePlayDone = useCallback(() => {
    playFromAppearanceClicked(lane, appearance, 'results_table');
  }, [playFromAppearanceClicked, lane, appearance]);

  const handleCopyLinkDone = useCallback(() => {
    copyLinkClicked(lane, appearance, 'results_table');
  }, [copyLinkClicked, lane, appearance]);

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      onMouseEnter={onHover}
      sx={{
        px: 1,
        py: 0.75,
        borderRadius: 1,
        bgcolor: highlighted ? 'action.selected' : 'transparent',
        transition: 'background-color 120ms',
      }}
    >
      <Typography variant="caption" sx={{ width: 150, flexShrink: 0 }}>
        <LDateTime value={toDateTimeStr(appearance.from)} showSeconds />
      </Typography>
      <Box sx={{ width: 100, flexShrink: 0 }}>
        <Chip
          size="small"
          variant="outlined"
          icon={
            isFace ? (
              <FaceOutlinedIcon color="secondary" />
            ) : (
              <RecordVoiceOverOutlinedIcon color="secondary" />
            )
          }
          label={
            isFace ? (
              <FormattedMessage id="logger.persons.source.face" defaultMessage="Rostro" />
            ) : (
              <FormattedMessage id="logger.persons.source.voice" defaultMessage="Voz" />
            )
          }
        />
      </Box>
      <Typography variant="caption" sx={{ width: 80, flexShrink: 0 }}>
        <Duration seconds={((appearance.to - appearance.from) / 1000) as Seconds} />
      </Typography>
      <Box sx={{ width: 120, flexShrink: 0 }}>
        <ConfidenceBadge
          level={
            appearance.source === 'voice'
              ? distanceToLevel(appearance.score)
              : confidenceToLevel(appearance.score)
          }
          score={appearance.score}
          inline
        />
      </Box>
      <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
        <PlayAtTimestampButton time={appearance.from} onDone={handlePlayDone} />
        <CopyLinkAtTimestampButton time={appearance.from} onDone={handleCopyLinkDone} />
      </Stack>
    </Stack>
  );
}
